/**
 * Organize Login page -- WorkOS Magic Auth flow
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { store, actions } from "@/lib/store";
import { sendCode, verifyCode, checkSession } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function Login() {
  const { isAuthenticated } = useSnapshot(store);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate("/", { replace: true });
    return null;
  }

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setError(null);

    const result = await sendCode(email.trim());
    setIsLoading(false);

    if (!result.ok) {
      setError(result.error || "Failed to send code");
      return;
    }

    setStep("code");
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;

    setIsLoading(true);
    setError(null);

    const result = await verifyCode(email.trim(), code.trim());

    if (!result.ok) {
      setError(result.error || "Invalid code");
      setIsLoading(false);
      return;
    }

    // Check session to confirm authentication
    const { user } = await checkSession();
    setIsLoading(false);

    if (user) {
      actions.setUser(user);
      navigate("/", { replace: true });
    } else {
      setError("Authentication failed");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-organize-bg p-6">
      <div className="w-full max-w-md">
        <div className="bg-organize-card border border-organize-border rounded-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="/vizi-logo.png"
              alt="Vizi"
              className="h-12 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-vizimix-100">
              Vizi Fan Mix
            </h1>
            <p className="text-vizimix-400 mt-1">Event Organizer Dashboard</p>
          </div>

          {/* Info banner */}
          <div className="bg-vizimix-900/30 border border-vizimix-700/30 rounded-lg p-4 mb-6 text-sm text-vizimix-300">
            Sign in to create and manage your video mix events.
          </div>

          {/* Error */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Email step */}
          {step === "email" && (
            <form onSubmit={handleSendCode}>
              <label className="block text-sm font-medium text-vizimix-200 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
                disabled={isLoading}
                className="mb-4"
              />
              <Button
                type="submit"
                disabled={isLoading || !email.trim()}
                className="w-full"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  "Send verification code"
                )}
              </Button>
            </form>
          )}

          {/* Code step */}
          {step === "code" && (
            <form onSubmit={handleVerifyCode}>
              <p className="text-center text-vizimix-300 mb-6">
                We sent a 6-digit code to
                <br />
                <strong className="text-vizimix-100">{email}</strong>
              </p>
              <label className="block text-sm font-medium text-vizimix-200 mb-2">
                Verification code
              </label>
              <Input
                type="text"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="123456"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                disabled={isLoading}
                className="mb-4 text-center text-2xl tracking-widest"
              />
              <Button
                type="submit"
                disabled={isLoading || code.length !== 6}
                className="w-full"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  "Verify & sign in"
                )}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setError(null);
                }}
                className="w-full mt-3 text-vizimix-400 hover:text-vizimix-300 text-sm"
              >
                Use a different email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
